import { DEFAULT_BANNER_STATUS } from '@imprivata-cloud/components';
import {
  CoreEventTypes,
  type FaceCeremony,
  useEmitters,
} from '@imprivata-cloud/core';
import {
  createContext,
  Fragment,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DISPLAY_BANNER_TIME, toDisplayStatus } from '../../utils/bannerUtils';
import { resolveCameraNotification } from '../camera';
import { errorConfiguration } from '../ErrorConfig';
import { ErrorView } from '../ErrorView';
import { type AppError, FatalError } from '../errors';
import { ErrorType } from '../types';

// The shape an emitter hands to `emitNotification`, mapped onto the core event
// body it raises (type -> level, message -> title, description -> message). The
// banner is drawn by the shared NotificationSystem, not by this provider.
interface NotificationBannerProps {
  description?: string;
  message?: string;
  key?: string;
  type?: 'error' | 'warning' | 'info' | 'success';
}

export interface NotificationState {
  error?: AppError;
}

interface ErrorConfig {
  overrideClasses?: string;
  hideCloseBtn?: boolean;
  persistOnInteraction?: boolean;
}

export const NotificationContext = createContext<
  | (NotificationState & {
      // Every banner is rendered by the shared NotificationSystem (mounted in
      // AuthnLayout); these emitters push to it via core's event bus. `overrideClasses`
      // lets a caller reposition the banner for a specific screen — the face-capture
      // flow places it over the video frame — without a second banner surface.
      emitNotification: (
        bannerProps: NotificationBannerProps,
        config?: ErrorConfig,
      ) => void;
      emitError: (error: AppError | string, config?: ErrorConfig) => void;
      // Camera conditions are emitted through their own entry point rather than
      // as an optional argument to `emitError`: the copy varies by ceremony, and
      // an optional ceremony would silently resolve to authentication wording on
      // an enrolling surface. Requiring it here keeps the other emitError
      // callsites — none of which are camera conditions — unchanged.
      emitCameraError: (
        code: string,
        ceremony: FaceCeremony,
        config?: ErrorConfig,
      ) => void;
      clearErrorNotifications: () => void;
    })
  | null
>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider',
    );
  }
  return context;
};

export type NotificationsProps = {
  translationRoot?: string;
};

// Context provider for banners and errors
export const Notifications = ({
  translationRoot,
  children,
}: PropsWithChildren<NotificationsProps>) => {
  const { t } = useTranslation();
  const [errorView, setErrorView] = useState<ReactNode>();
  const { emitUIEvent } = useEmitters();

  // Emit a banner to the shared NotificationSystem. `NotificationBannerProps`
  // maps onto the core event body: type -> status, message -> title, description
  // -> message.
  const emitNotification = useCallback(
    (message: NotificationBannerProps, config?: ErrorConfig) => {
      emitUIEvent(CoreEventTypes.UI_NOTIFY, {
        // Error-severity banners are shown one notch softer (warning styling);
        // see toDisplayStatus.
        level: toDisplayStatus(message.type ?? DEFAULT_BANNER_STATUS),
        title: message.message,
        message: message.description,
        closeTimeInSec: DISPLAY_BANNER_TIME,
        overrideClasses: config?.overrideClasses,
        hideCloseBtn: config?.hideCloseBtn,
        persistOnInteraction: config?.persistOnInteraction,
      });
    },
    [emitUIEvent],
  );

  const clearErrorNotifications = useCallback(() => {
    emitUIEvent(CoreEventTypes.CLEAR);
  }, [emitUIEvent]);

  // Use this to handle errors
  const emitError = useCallback(
    (err: AppError | string, config?: ErrorConfig) => {
      let errorCode: string;
      if (typeof err !== 'string') {
        // Don't show banners on fatal errors
        if (err instanceof FatalError) {
          setErrorView(<ErrorView />);
          return;
        }
        // Pull out error code
        errorCode = err?.code || err?.details?.code;
      } else {
        errorCode = err;
      }
      console.debug('[Notification Context: ] error code', errorCode);
      const errorConfig =
        errorConfiguration && errorCode in errorConfiguration
          ? errorConfiguration[errorCode]
          : undefined;
      if (errorConfig) {
        switch (errorConfig.errorType) {
          case ErrorType.FULL_PAGE:
            setErrorView(
              errorConfig.errorView ? errorConfig.errorView : <ErrorView />,
            );
            break;
          case ErrorType.GLOBAL:
            {
              // Create i18n key for error message lookup
              const errorKey = translationRoot
                ? `${translationRoot}.errors.${errorConfig.errorCode}`
                : `errors.${errorConfig.errorCode}`;
              const description =
                translationRoot && errorConfig.description
                  ? t(`${translationRoot}.errors.${errorConfig.description}`)
                  : undefined;
              // Initialize banner message
              //
              // NOTE: This is the default banner. If a case does not match, a banner
              // will still be shown! To prevent this, add codes or messages to the `errorsWithoutBanner`
              // array above.
              const bannerMsg: NotificationBannerProps = {
                key: errorKey,
                type: errorConfig.type,
                description,
                message: t(errorKey as never),
              };

              // Fallback in case translation fails
              if (bannerMsg.message === errorKey) {
                console.debug('[BANNER] Fallback on translation', {
                  bannerMsg,
                });
                bannerMsg.message = translationRoot
                  ? t(`${translationRoot}.errors.something-unexpected`)
                  : t('errors.something-unexpected');
              }

              emitNotification(bannerMsg, config);
            }
            break;
          default:
            console.error('Unhandled error type', { errorConfig });
        }
      } else {
        console.debug(
          '[Notification Context: ] error config not found for error code: ',
          errorCode,
        );
        emitNotification(
          {
            key: 'unknown-error',
            type: 'error',
            message: translationRoot
              ? t(`${translationRoot}.errors.something-unexpected`)
              : t('errors.something-unexpected'),
          },
          config,
        );
      }
    },
    [t, translationRoot, emitNotification],
  );

  // Camera copy is resolved by core's notification map instead of this provider's
  // `errorConfiguration` lookup, because a blocked camera reads differently while
  // enrolling than while authenticating — a pairing a map keyed on the code alone
  // cannot express. The keys core names live at the bundle root (`errors.*`), so a
  // container with its own translation root reroots them here.
  const emitCameraError = useCallback(
    (code: string, ceremony: FaceCeremony, config?: ErrorConfig) => {
      const camera = resolveCameraNotification(
        key => t(translationRoot ? `${translationRoot}.${key}` : key),
        ceremony,
        code,
      );
      if (!camera) {
        emitError(code, config);
        return;
      }
      emitNotification(
        {
          key: `errors.${code}`,
          // Severity comes from the same map as the copy, so the two cannot
          // disagree about a condition the way `errorConfiguration` could.
          type: camera.level,
          message: camera.title,
          description: camera.message,
        },
        // Camera banners carry no close affordance — they dismiss only once the
        // condition resolves — so this is the one path that hides the button.
        { ...config, hideCloseBtn: true },
      );
    },
    [t, translationRoot, emitError, emitNotification],
  );

  const value = useMemo(
    () => ({
      emitNotification,
      emitError,
      emitCameraError,
      clearErrorNotifications,
    }),
    [clearErrorNotifications, emitNotification, emitError, emitCameraError],
  );

  if (errorView) {
    return <Fragment key={'error-view'}>{errorView}</Fragment>;
  }

  return (
    <NotificationContext.Provider value={value}>
      <Fragment key={'initial-notif-render'}>{children}</Fragment>
    </NotificationContext.Provider>
  );
};
